import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, MapPin } from 'lucide-react';
import { getEloLevel } from '../../engine';
import { Padel24Logo } from '../ui/Padel24Logo';
import { useCurrentMember, mustPickIdentity } from '../../state/useCurrentMember';
import { usePushRegistration } from '../../state/usePushRegistration';
import { useMessaging } from '../../state/useMessaging';
import { conversationById, unreadCount } from '../../engine/messaging';
import {
  dynamicRuleOf,
  inboxFor,
  inboxUnread,
  isMessagingDisabled,
  messagingPlayers,
} from '../../engine/messagingGroups';
import { CLUB_SENDER_ID } from '../../engine/messaging';
import { useCenterPrefs } from './useCenterPrefs';
import {
  centersFrom,
  memberActivities,
  memberBookings,
  memberLiveTournaments,
  nextForMember,
  memberClubsOf,
  memberTournamentHistory,
} from './memberData';
import { initials } from './mobileUi';
import { useT } from '../../i18n';
import { MemberPicker } from './MemberPicker';
import { RecipientPicker } from './RecipientPicker';
import { MobileTabBar, type MobileTab } from './MobileTabBar';
import { MobileHome } from './screens/MobileHome';
import { MobileMatches } from './screens/MobileMatches';
import { MobileProfile } from './screens/MobileProfile';
import { MobileCenters } from './screens/MobileCenters';
import { MobileCenterDetail } from './screens/MobileCenterDetail';
import { MobileReservations } from './screens/MobileReservations';
import { MobileElo } from './screens/MobileElo';
import { MobileMessages } from './screens/MobileMessages';
import { MobileChat } from './screens/MobileChat';
import { MobileFindClub } from './screens/MobileFindClub';
import { ClubContextNeeded, ClubContextSheet, type ManagedOrganization } from './ClubContext';
import { AcrossClubsWithoutClub } from './AcrossClubs';
import { AdminMenu } from './AdminMenu';
import { ClubLoadState, IdentityState } from './ShellStates';
import { PasskeySection } from '../account/PasskeySection';
import { usePlayerClubs } from '../../state/usePlayerClubs';
import type { PlayerClub } from '../../lib/api/customerContext';
import type { MyClub } from '../../lib/api/myClubs';
import { HIT_AREA_44 } from '../ui/hitArea';

export interface MobileAppProps {
  // ── THE ACTIVE CLUB CONTEXT (owner, 2026-10-16) ──────────────────
  //
  // The app is the same app with zero, one or many memberships. Which club is
  // active is a context the 📍 selector sets for the features that need one;
  // the personal tabs work without it.
  /** The club the player selected, or null for none. */
  activeClub?: PlayerClub | null;
  /**
   * May this session read the club's correspondence (#236, 2026-10-31)? The
   * shell decides from the capability union `/me` reports — the same answer
   * the portal's Meldinger icon is drawn on — and the hook then reads nothing
   * when the answer is no. Default true: off the API, and for the member
   * surface, which reads nothing here anyway (#393).
   */
  messagingEnabled?: boolean;
  /**
   * Change the active club in place — `null` clears it. The app stays mounted
   * and the store reloads that club's data.
   *
   * NULL WHERE THERE IS NO CONTEXT TO CHOOSE: the club portal's preview of
   * the app (the administrator's own club) and a legacy member session (the
   * session's own club). There the app behaves exactly as before.
   */
  onSelectClub?: ((club: PlayerClub | null) => void) | null;
  /** Organizations this account may administer (`/auth/scopes`) — web only. */
  organizations?: ManagedOrganization[];
  onEnterOrganization?: (tenantId: string) => void;
  /** Sign out. Offered only to a proven person — never on the portal preview. */
  onSignOut?: (() => void) | null;
  /**
   * How the club's own load is going (#801 review).
   *
   * Null where nothing loads a club this way — the club portal, the memory
   * backend — and then the states below do not apply.
   */
  memberBootstrap?: {
    status: 'idle' | 'loading' | 'ready' | 'failed';
    active: boolean;
    retry: () => void;
    /** False until the club has saved its opening hours (owner decision 2026-10-31). */
    bookingOpen?: boolean;
  } | null;
  effectivePlayerDB: any[];
  bookings: any[];
  plannedMatches: any[];
  singleMatches: any[];
  activities: any[];
  archiveWithLive: any[];
  /**
   * The member's own history, as `/member/bootstrap` computed it (#185).
   *
   * Preferred over deriving one here when present — the server has already run
   * `getPlayerTournamentHistory` and field-filtered the result so one member's
   * payload carries no other member's details. Deriving it again from
   * `archiveWithLive` would need the whole tournament documents, which a member
   * is deliberately not given.
   */
  memberHistory?: any[];
  /** The signed-in member as `/member/bootstrap` describes them (#185). */
  memberMe?: any;
  bookingConfig: any;
  bookingZones: any[];
  clubInfo: any;
  addBooking: (data: any, opts?: any) => Promise<any>;
  /** The venue's equipment and its reservations, offered beside a court (#911). */
  assets?: any[];
  assetBookings?: any[];
  /** The member equipment command — a machine booked on its own (#911 slice 2, § 6b). */
  addAssetBooking?: (intent: any) => Promise<any>;
  persistActivities: (next: any[]) => any;
  /** Adopt an activity the server changed (#245). */
  applyServerActivity: (updated: any) => void;
  getPlayerTournamentHistory: (playerId: any, archive: any, playerName?: any) => any[];
  calcPlayerPositionInTournament: (playerId: any, t: any) => any;
  /**
   * Result recording for LIVE tournaments — the same shared handlers the admin
   * app and the Banepad use (useTournamentResultRecording). Optional: without
   * them the app is read-only for ongoing tournaments.
   */
  recordResultForTournament?: (
    tid: any,
    matchId: any,
    score1: number,
    score2: number,
    source: string,
  ) => void;
  recordKMResultForTournament?: (tid: any, updatedMatch: any) => void;
  /** Planned single matches (usePlannedMatchActions) — register on a booking, join/leave. */
  addPlannedMatch?: (input: any) => Promise<any>;
  updatePlannedMatchPlayers?: (id: any, teams: { team1: any; team2: any }) => Promise<any>;
  deletePlannedMatch?: (id: any) => Promise<any>;
  /** Record the result of a planned single match (completePlannedMatch + addSingleMatch, wired in AppShell). */
  completePlannedMatch?: (id: any, data: any) => Promise<any>;
  /** Start on this tab (defaults to «Hjem»). */
  initialTab?: MobileTab;
}

// ── THE 📍 SELECTOR, AT THREE WIDTHS ──────────────────────────────
// Phone: the small mono pill in the header. Rail: an icon button. Wide: the
// club card at the top of the sidebar. One button, restyled by the app's own
// width (container queries) — never a second copy.
const CLUB_BUTTON =
  // A 44 px tap target on the phone without moving the strip (#1144).
  HIT_AREA_44 +
  ' font-mono text-[10px] uppercase tracking-wider text-ink-3 flex items-center gap-1 max-w-[150px] min-w-0 rounded-xl ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0f1611] ' +
  '@rail:p-2 @rail:max-w-none @rail:hover:bg-[#eef0ea] ' +
  '@wide:w-full @wide:gap-2.5 @wide:px-3 @wide:py-2.5 @wide:bg-[#f7f8f5] @wide:border @wide:border-[#e7eae3] ' +
  // `[font-family:inherit]`: the card reads in the body face; `font-body` is
  // hand-written CSS, so there is no Tailwind variant of it to switch to.
  '@wide:normal-case @wide:tracking-normal @wide:[font-family:inherit] @wide:text-[14px] @wide:font-semibold @wide:text-[#0f1611] @wide:text-left';
const CLUB_PIN = 'flex-shrink-0 @rail:w-[18px] @rail:h-[18px] @wide:w-[16px] @wide:h-[16px]';
const CLUB_TEXT = 'min-w-0 @rail:hidden @wide:block';

/** A membership as the context it selects — exactly the fields `PlayerClub` declares. */
const asContext = (club: MyClub): PlayerClub => ({ id: club.id, slug: club.slug, name: club.name });

type Sheet = 'member' | 'newMessage' | 'club' | null;
type SubScreen = 'centers' | 'center' | 'elo' | 'chat' | 'findClub' | null;

// ─────────────────────────────────────────────────────────────────────
// MobileApp — the member-facing app: a full-height column with the screen in
// the middle and the tab bar at the bottom. It knows nothing about the phone
// frame it is previewed in; ship it on its own and it just fills the screen.
//
// Identity is useCurrentMember (the shared «logged in as» pick); every «my»
// list is derived by the pure memberData helpers from the same stores the
// desktop views use — no parallel data, no second source of truth.
// ─────────────────────────────────────────────────────────────────────
export function MobileApp({
  activeClub = null,
  messagingEnabled = true,
  onSelectClub = null,
  organizations = [],
  onEnterOrganization,
  onSignOut = null,
  memberBootstrap = null,
  effectivePlayerDB,
  bookings,
  plannedMatches,
  singleMatches,
  activities,
  archiveWithLive,
  memberHistory,
  memberMe,
  bookingConfig,
  bookingZones,
  clubInfo,
  addBooking,
  assets = [],
  assetBookings = [],
  addAssetBooking,
  persistActivities,
  applyServerActivity,
  getPlayerTournamentHistory,
  calcPlayerPositionInTournament,
  recordResultForTournament,
  recordKMResultForTournament,
  addPlannedMatch,
  updatePlannedMatchPlayers,
  deletePlannedMatch,
  completePlannedMatch,
  initialTab = 'home',
}: MobileAppProps) {
  // `tr`, not `t`: this file already binds `t` to a TAB and to a tournament in
  // four inner functions, and a translator that those shadow is a trap waiting
  // for the next edit. Same reason and same name as AllMatchesView (#310).
  const tr = useT();
  const [tab, setTabState] = useState<MobileTab>(initialTab);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [sub, setSub] = useState<SubScreen>(null);
  const [centerId, setCenterId] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [matchesSegment, setMatchesSegment] = useState<string | null>(null);
  const [playTab, setPlayTab] = useState<'about' | 'play' | 'activities' | 'matches'>('play');
  // THE ID THE LIST IS KEYED BY (#185). The session carries the database uuid;
  // `me.id` is the legacy id every row in `effectivePlayerDB` uses. Resolving
  // against the wrong one greeted a member by another member's name, with that
  // member's level and rating on the screen.
  const { member, memberId, setMemberId, loaded, proven, identityFailed, retryIdentity } =
    useCurrentMember(effectivePlayerDB, memberMe?.id ?? null);
  const { prefs, setPrefs } = useCenterPrefs();

  // ── MEMBERSHIPS AND THE ACTIVE CLUB (owner, 2026-10-16) ──────────
  //
  // `canSelectClub` is the whole switch: only an account on the player
  // surface has a context to choose. Everywhere else the app behaves as it
  // always has, with the club it was given.
  const canSelectClub = onSelectClub != null;
  const memberships = usePlayerClubs(canSelectClub);
  const noClub = canSelectClub && activeClub == null;
  // ── THE ONE-CLUB DEFAULT IS A CONTEXT, NOT AN ENTRY ───────────────
  //
  // A player with exactly one membership gets that club as the active
  // context when the memberships answer. It moves them nowhere: the tab they
  // are on stays, the app stays, and nothing is shown in between.
  //
  // IT IS NEVER RE-APPLIED, and the mechanism is the dependency list: this
  // runs when the memberships ANSWER, which is once per app life (again only
  // after a failed read is retried — still the first answer). It does not
  // run when the context changes, so a player who clears it is not overruled
  // by a default coming back. Adding `activeClub` to the dependencies would
  // break exactly that, and `mobile-club-context` fails if it does.
  useEffect(() => {
    if (!onSelectClub || memberships.status !== 'ready') return;
    const only = memberships.clubs.length === 1 ? memberships.clubs[0] : null;
    if (activeClub == null && only) onSelectClub(asContext(only));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberships.status]);
  // Push notifications — only meaningful inside the native shell (apps/mobile).
  const push = usePushRegistration(member?.id);
  // Messaging — polled while the inbox or a thread is on screen.
  const messaging = useMessaging({
    enabled: messagingEnabled,
    live: tab === 'messages' || sub === 'chat',
  });
  const clubs = useMemo(() => memberClubsOf(clubInfo), [clubInfo]);
  const groupCtx = useMemo(
    () => ({ players: effectivePlayerDB || [], clubs }),
    [effectivePlayerDB, clubs],
  );
  const unread = member ? inboxUnread(messaging.state, member.id, groupCtx) : 0;
  // The conversation on screen: stored, or one of the member's virtual automatic groups.
  const chatConversation = useMemo(() => {
    if (!chatId) return null;
    if (!member) return conversationById(messaging.state, chatId);
    return (
      inboxFor(messaging.state, member.id, groupCtx).find((c) => c.id === chatId) ||
      conversationById(messaging.state, chatId)
    );
  }, [chatId, member, messaging.state, groupCtx]);
  const openChat = async (id: string) => {
    // An automatic group gets its stored record on first open, so sending works.
    const rule = dynamicRuleOf(id);
    if (rule && !conversationById(messaging.state, id)) await messaging.openDynamic(rule);
    setChatId(id);
    setSub('chat');
  };
  // An open thread is read — also when new messages arrive while it is open.
  useEffect(() => {
    if (sub !== 'chat' || !chatId || !member) return;
    const c = chatConversation;
    if (c && unreadCount(messaging.state, c, member.id) > 0) void messaging.read(chatId, member.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, chatId, messaging.state, member?.id, chatConversation]);
  const courtMeta = bookingConfig?.courtMeta || {};
  const players = effectivePlayerDB || [];

  // ── A CLUB CHANGE KEEPS THE APP, AND DROPS WHAT BELONGED TO THE CLUB ──
  //
  // The tab survives; a centre page, an open thread or a «Kamper» segment
  // named something of the previous club's and would point at nothing — or
  // worse, at a same-named thing in the next one. The store has already
  // emptied the club's data (`useMemberBootstrap`).
  const activeClubId = activeClub?.id ?? null;
  const previousClubId = useRef(activeClubId);
  useEffect(() => {
    if (previousClubId.current === activeClubId) return;
    previousClubId.current = activeClubId;
    setCenterId(null);
    setChatId(null);
    setMatchesSegment(null);
    setSub((s) => (s === 'center' || s === 'chat' || s === 'centers' ? null : s));
  }, [activeClubId]);

  const chooseClub = (club: MyClub | null) => {
    setSheet(null);
    if (sub === 'findClub') setSub(null);
    // LEAVING A CLUB RETURNS TO THE PERSONAL HOME — the app's own Hjem, not a
    // screen in front of it.
    if (!club) setTabState('home');
    onSelectClub?.(club ? asContext(club) : null);
  };
  const openClubSheet = () => setSheet('club');

  const setTab = (t: MobileTab) => {
    setSub(null);
    setMatchesSegment(null);
    setTabState(t);
  };
  const goMatches = (segment: string) => {
    setSub(null);
    setMatchesSegment(segment);
    setTabState('matches');
  };
  const goPlay = (t: 'about' | 'play' | 'activities' | 'matches') => {
    setSub(null);
    setPlayTab(t);
    setTabState('play');
  };
  const openCenter = (id: string) => {
    setCenterId(id);
    setSub('center');
  };
  // In-app back: a centre page → the centre list → the tab; a tab → «Hjem».
  // Leaving the preview is the frame's job (see PhoneFrame), never this arrow.
  const canGoBack = sub != null || tab !== 'home';
  const goBack = () => {
    if (sub === 'center') setSub('centers');
    else if (sub != null) setSub(null);
    else setTab('home');
  };

  // Padel centres: the home club + every venue an activity names.
  //
  // NONE WITHOUT A CLUB'S OWN DATA. `centersFrom` always adds the home venue,
  // named from `clubInfo` — which with no club, or while the selected club is
  // still loading (the store masks it, #848 review), is the default record, so
  // the list would offer a centre that is nobody's.
  const clubDataPending =
    noClub || memberBootstrap?.status === 'loading' || memberBootstrap?.status === 'failed';
  const centers = useMemo(
    () => (clubDataPending ? [] : centersFrom(activities, clubInfo, bookings)),
    [clubDataPending, activities, clubInfo, bookings],
  );
  const favoriteCenter =
    centers.find((c) => c.id === prefs.favoriteId) || centers.find((c) => c.isHome) || null;
  const centerFilter = centers.some((c) => c.id === prefs.filterId) ? prefs.filterId : null;

  const myBookings = useMemo(
    () => memberBookings({ bookings, plannedMatches, activities, member }),
    [bookings, plannedMatches, activities, member],
  );
  const myActivities = useMemo(
    () => memberActivities(activities, member?.id, bookings),
    [activities, member, bookings],
  );
  const next = useMemo(() => nextForMember(myBookings, myActivities), [myBookings, myActivities]);
  const history = useMemo(() => {
    if (!member) return [];
    // THE SERVER'S ANSWER WHEN THERE IS ONE (#185) — see `memberTournamentHistory`.
    return memberTournamentHistory(memberHistory, () => {
      try {
        return getPlayerTournamentHistory(member.id, archiveWithLive || [], member.name) || [];
      } catch {
        return [];
      }
    });
  }, [member, memberHistory, archiveWithLive, getPlayerTournamentHistory]);
  const liveAll = useMemo(() => memberLiveTournaments(history), [history]);
  // Seriespill has its own tab; everything else is «Pågår» under Kamper / Hjem.
  const isLeague = (t: any) => t?.type === 'seriespill';
  const live = useMemo(() => liveAll.filter((l) => !isLeague(l.tournament)), [liveAll]);
  const leagueLive = useMemo(() => liveAll.filter((l) => isLeague(l.tournament)), [liveAll]);
  const leaguePast = useMemo(
    () =>
      (history || []).filter(
        (h: any) =>
          isLeague(h.tournament) && !(h.tournament?.isLive && h.tournament?.status !== 'done'),
      ),
    [history],
  );
  const historyNoLeague = useMemo(
    () => (history || []).filter((h: any) => !isLeague(h.tournament)),
    [history],
  );
  // The member's next unplayed tournament match: first open match of the first
  // started live tournament (non-league before league, both count).
  const nextMatch = useMemo(() => {
    const started = [...live, ...leagueLive].filter((l) => l.started && l.open.length > 0);
    return started.length ? { live: started[0], match: started[0].open[0] } : null;
  }, [live, leagueLive]);

  // First launch: nobody picked yet → the app asks who you are before anything
  // else. (Effects never run in static renders, so tests see the plain shell.)
  //
  // NEVER FOR A PROVEN IDENTITY (#791). The picker is forced —
  // `dismissible={!mustPick}` — so reaching it with a session that already
  // names the person would mean asking a verified player to choose who to be,
  // and `setMemberId` would honour the choice. `proven` is false on the club
  // surface and on the memory backend, where picking IS the honest answer; it
  // is true for a member session and for an account on the player surface,
  // where the server has already answered.
  //
  // IT STAYS FALSE WHEN THE IDENTITY READ FAILED. The person is still proven;
  // what is missing is the answer, and offering a picker then would turn a
  // retryable failure into an identity choice.
  const mustPick = mustPickIdentity({ loaded, proven, memberId, playerCount: players.length });
  const lvl = getEloLevel(Math.round(member?.elo || 0));

  // ── A PROVEN PERSON THE APP CANNOT NAME (#800) ────────────────────
  //
  // The dead end the gate left behind. For a proven identity there is no
  // picker and no switch — correctly — so somebody whose identity read did not
  // answer got an app with a disabled avatar, no name, and every «my» screen
  // empty, with nothing saying why. The empty screens are the lie: they look
  // like a player with no bookings.
  //
  // TWO CASES, TWO SENTENCES, because the remedies differ. «We could not ask»
  // is retryable and says so; «you have no profile yet» is a state, and the
  // way out of it is to create one — which the home does, so the way out is to
  // go back there.
  const identityMissing = proven && loaded && !member;

  // ── THE CLUB'S LOAD COMES FIRST, AND THE ORDER IS THE FIX ─────────
  //
  // #801 review. `member` is resolved against THIS CLUB'S ROSTER, which the
  // bootstrap supplies — so while that request is in flight or has failed, a
  // person whose `/me/player` answered perfectly still resolves to nobody,
  // and the «no player profile yet» sentence above was shown to somebody who
  // has one. A wrong diagnosis is worse than a blank screen: it tells them
  // something false about their account.
  //
  // So the club's own state is answered BEFORE the identity's. «We could not
  // load the club» and «we could not find out who you are» are different
  // sentences with different retries, and neither may be shown in place of
  // the other.
  // THE STATUS IS ENOUGH, and the `active` term I first wrote beside it was
  // redundant: `useMemberBootstrap` reports `idle` whenever it is not active,
  // so `loading` already means «this surface loads a club, and it is in
  // flight». A mutation removing the term survived — correctly — and the
  // honest response to an untestable condition is to delete it rather than
  // contrive a case the product cannot reach.
  const clubLoading = memberBootstrap?.status === 'loading';
  const clubFailed = memberBootstrap?.status === 'failed';

  const centerDetailProps = {
    member,
    effectivePlayerDB: players,
    bookings,
    plannedMatches,
    activities,
    bookingConfig,
    bookingOpen: memberBootstrap?.bookingOpen !== false,
    bookingZones,
    clubInfo,
    addBooking,
    assets,
    assetBookings,
    addAssetBooking,
    persistActivities,
    applyServerActivity,
    addPlannedMatch,
    updatePlannedMatchPlayers,
    deletePlannedMatch,
    onFavorite: (id: string | null) => setPrefs({ favoriteId: id }),
  };

  // ── IN PLACE OF A SCREEN, NEVER IN PLACE OF THE APP ──────────────
  //
  // These were full-screen early returns, which made a failed club load a
  // dead end with no header and no tabs. Now they fill the content area and
  // the shell stays: the 📍 selector can move to another club, and the
  // personal tabs remain.
  //
  // THE ORDER IS STILL THE FIX (#801 review): the club's state is answered
  // before the identity's. The one exception is a PERSONAL tab for somebody
  // the app can already name — an account's own player does not depend on
  // the club's roster, so Hjem and Profil do not wait for it.
  const clubState: 'loading' | 'failed' | null = clubLoading
    ? 'loading'
    : clubFailed
      ? 'failed'
      : null;
  const personalTab = tab === 'home' || tab === 'profile';
  // THE CLUB'S SUB-SCREENS WAIT TOO (#848 review). A centre page, a thread
  // and the ELO screen are made of the club's data and its actions, so while
  // the club is not ready they show its state rather than rendering — only
  // «Finn en klubb», which is the way OUT of a club that will not load, stays.
  const clubSubScreen = sub != null && sub !== 'findClub';
  const clubBlocks = clubState != null && (clubSubScreen || !(personalTab && member));
  const clubContextProps = {
    memberships,
    activeClubId,
    onSelect: chooseClub,
    onFindClub: () => {
      setSheet(null);
      setSub('findClub');
    },
    organizations,
    onEnterOrganization,
  };
  // Sign-out lives in Profil; a panel standing in for Profil carries it.
  const panelSignOut = tab === 'profile' && proven ? onSignOut : null;
  // ── PASSKEYS ARE THE ACCOUNT'S, NOT THE PLAYER PROFILE'S ─────────
  //
  // So they are offered wherever an account manages itself in Profil: in
  // `MobileProfile`, and ALSO under «Opprett spillerprofil» when the account
  // has no player yet — the same reason `panelSignOut` exists for the panels
  // that stand in for Profil. Only for an account on the player surface
  // (`canSelectClub`): a legacy member session holds no credential of its own.
  const accountPasskeys = proven && canSelectClub;
  const clubTabNeedsContext =
    noClub && (tab === 'play' || tab === 'reservations' || tab === 'matches');
  // «ALLE KLUBBER» WITHOUT A CLUB (#760, owner decision 2026-11-02): with no
  // club chosen, Reservasjoner and Kamper show the person's own history across
  // clubs instead of «Velg en klubb» — for the same accounts the segment is
  // offered to. Spill needs a club, so it keeps the choice.
  const historyWithoutClub =
    clubTabNeedsContext && canSelectClub && proven && (tab === 'reservations' || tab === 'matches');

  return (
    <div
      className="player-app relative h-full font-body"
      style={{ background: '#f7f8f5', color: '#0f1611' }}
    >
      {/* ONE GRID, THREE ARRANGEMENTS — `.player-shell` in index.css. Every
          control below exists once; the grid moves it between the phone's
          header row and bottom bar, the 80 px rail and the wide sidebar. */}
      <div className="player-shell">
        <div className="ps-side" aria-hidden="true" />
        <div className="ps-brand items-center px-4 pt-5 pb-3 @wide:px-5">
          <span className="@wide:hidden">
            <Padel24Logo size="sm" variant="icon" />
          </span>
          <span className="hidden @wide:inline">
            <Padel24Logo size="sm" />
          </span>
        </div>
        <div className="ps-back pl-4 pt-1 pb-1.5 @rail:pl-0 @rail:pt-4 @rail:pb-1">
          {/* Aligned to the SCREEN's column, so the arrow sits over the
              content it leads back from rather than at the far edge. */}
          <div className="flex items-center gap-2 min-w-0 w-full mx-auto @rail:max-w-[720px] @rail:pl-6 @wide:max-w-[1120px] @wide:pl-8 @huge:max-w-[1280px]">
            {canGoBack && (
              <button
                onClick={goBack}
                className="text-ink-3 -ml-1 p-1 flex-shrink-0 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0f1611]"
                aria-label={tr('mobile.common.back')}
              >
                <ArrowLeft size={18} />
              </button>
            )}
            {/* The page logo of the phone header. On the rail and the sidebar
              the brand cell carries it instead. */}
            {(tab !== 'home' || sub != null) && (
              <span className="@rail:hidden">
                <Padel24Logo size="sm" variant="icon" />
              </span>
            )}
          </div>
        </div>
        <div className="ps-club flex items-center min-w-0 pt-1 pb-1.5 @rail:justify-center @rail:px-2 @rail:py-2 @wide:block @wide:px-4">
          {canSelectClub ? (
            // THE 📍 SELECTOR (owner, 2026-10-16). Always present for an
            // account, with or without a club — choosing one is never a step
            // before the app, so the control that does it is in the app.
            <button
              onClick={openClubSheet}
              className={CLUB_BUTTON}
              // LABEL IN NAME (#1144, WCAG 2.5.3): the name carries the text
              // the button SHOWS. With no club that is «Velg klubb», which
              // «Ingen klubb» was not — a voice user saying what they see
              // could not reach the control.
              aria-label={tr('mobile.clubContext.selectorAria', {
                name: activeClub?.name ?? tr('mobile.clubContext.choose'),
              })}
              aria-haspopup="dialog"
            >
              <MapPin size={11} className={CLUB_PIN} />
              <span className={CLUB_TEXT}>
                <span className="hidden @wide:block font-mono text-[10px] uppercase tracking-wider text-ink-3">
                  {tr('mobile.clubContext.title')}
                </span>
                <span className="block truncate">
                  {activeClub ? activeClub.name : tr('mobile.clubContext.choose')}
                </span>
              </span>
            </button>
          ) : (
            favoriteCenter && (
              <button
                onClick={() => setSub('centers')}
                className={CLUB_BUTTON}
                title={tr('mobile.common.centres')}
              >
                <MapPin size={11} className={CLUB_PIN} />
                <span className={CLUB_TEXT}>
                  <span className="block truncate">{favoriteCenter.name}</span>
                </span>
              </button>
            )
          )}
        </div>
        {/* «ADMINISTRASJON» (owner, 2026-10-16): its own control on the rail
            and the sidebar, apart from the 📍 club selector — administering is
            `/auth/scopes`, playing is `/me/clubs`. An account only; never
            dependent on membership; the native shell passes no organizations. */}
        {canSelectClub && onEnterOrganization && organizations.length > 0 && (
          <div className="ps-admin px-2 py-1 @wide:px-4 @wide:py-2">
            <AdminMenu organizations={organizations} onEnterOrganization={onEnterOrganization} />
          </div>
        )}
        <div className="ps-avatar flex items-center gap-3 min-w-0 pr-4 pt-1 pb-1.5 @rail:justify-center @rail:p-3 @rail:pb-5 @wide:justify-start @wide:px-5">
          <button
            // NO DEAD ACTION FOR A PROVEN IDENTITY (#791 review). With no
            // member resolved this opened the picker — which `!proven` now
            // refuses to render, so the control would do nothing at all. The
            // same reasoning as the profile's switch: absent beats inert.
            //
            // A proven person with no member is «we could not ask» or «no
            // profile yet» (`identityFailed`), and the answer to that is a
            // retry, not an identity choice. That state belongs with the
            // workspace-navigation slice; until then the avatar simply offers
            // nothing rather than pretending to.
            onClick={
              member ? () => setTab('profile') : proven ? undefined : () => setSheet('member')
            }
            disabled={!member && proven}
            aria-label={
              member
                ? // The initials the avatar SHOWS are in its name too (2.5.3).
                  tr('mobile.app.profileAria', {
                    name: member.name,
                    initials: initials(member.name),
                  })
                : tr('mobile.app.pickUser')
            }
            className="flex items-center justify-center rounded-full font-display font-semibold flex-shrink-0"
            style={{
              width: 30,
              height: 30,
              background: member ? lvl.color : '#dfe3da',
              color: member ? lvl.text : '#0f1611',
              fontSize: 11,
            }}
          >
            {member ? initials(member.name) : '?'}
          </button>
          {member && (
            // Beside the avatar on the wide sidebar only — the profile chip.
            // Presentation: the button above is the control and carries the name.
            <span className="hidden @wide:block min-w-0 text-left" aria-hidden="true">
              <span className="block text-[13px] font-semibold text-ink truncate">
                {member.name}
              </span>
              <span className="block text-[11px] text-ink-3">{tr('mobile.tab.profile')}</span>
            </span>
          )}
        </div>

        {/* Screen — a centred column that grows with the app, never stretched
            edge to edge: 720 on the rail, 1120 wide, 1280 at the widest. */}
        <div className="ps-main overflow-y-auto scrollbar-thin" style={{ paddingBottom: 8 }}>
          <div className="mx-auto w-full @rail:max-w-[720px] @rail:px-2 @rail:pt-1 @wide:max-w-[1120px] @wide:px-4 @huge:max-w-[1280px]">
            {clubBlocks && clubState ? (
              <ClubLoadState
                status={clubState}
                onRetry={memberBootstrap?.retry}
                onChooseClub={canSelectClub ? openClubSheet : null}
                onSignOut={panelSignOut}
              />
            ) : sub === 'chat' && chatId && chatConversation ? (
              <MobileChat
                key={chatId}
                state={messaging.state}
                conversation={chatConversation}
                member={member}
                effectivePlayerDB={players}
                clubInfo={clubInfo}
                onSend={(text) => messaging.send(chatId, member?.id, text)}
                clubs={clubs}
                onMute={(id, muted) => messaging.mute(member?.id, id, muted)}
                onLeaveDynamic={async (clubId) => {
                  await messaging.leaveDynamic(member?.id, clubId, true);
                  setSub(null);
                }}
              />
            ) : sub === 'elo' ? (
              <MobileElo
                member={member}
                effectivePlayerDB={players}
                singleMatches={singleMatches}
                history={history}
              />
            ) : sub === 'findClub' ? (
              <MobileFindClub
                memberships={memberships.clubs}
                onSelect={chooseClub}
                // A VENUE PAGE'S «BOOK BANE» (#833) is the member booking that
                // already exists: select the club, then its Spill › Book.
                onBook={(club) => {
                  chooseClub(club);
                  goPlay('play');
                }}
                onBack={() => setSub(null)}
              />
            ) : sub === 'center' && centers.find((c) => c.id === centerId) ? (
              <MobileCenterDetail
                key={centerId}
                center={centers.find((c) => c.id === centerId)!}
                isFavorite={prefs.favoriteId === centerId}
                onBack={() => setSub('centers')}
                {...centerDetailProps}
              />
            ) : sub != null ? (
              <MobileCenters
                centers={centers}
                favoriteId={prefs.favoriteId}
                filterId={centerFilter}
                onFavorite={(id) => setPrefs({ favoriteId: id })}
                onFilter={(id) => setPrefs({ filterId: id })}
                onOpen={openCenter}
                onBack={() => setSub(null)}
              />
            ) : identityMissing ? (
              <>
                <IdentityState
                  failed={identityFailed}
                  onRetry={retryIdentity}
                  canCreate={canSelectClub}
                  onSignOut={panelSignOut}
                />
                {/* Under the panel, not inside it: the panel is centred prose
                    about the PLAYER profile, and this is the account's. */}
                {tab === 'profile' && accountPasskeys && (
                  <div className="px-4 pb-4">
                    <PasskeySection />
                  </div>
                )}
              </>
            ) : historyWithoutClub ? (
              <AcrossClubsWithoutClub
                tab={tab === 'matches' ? 'matches' : 'reservations'}
                onChooseClub={openClubSheet}
              />
            ) : clubTabNeedsContext ? (
              <ClubContextNeeded {...clubContextProps} />
            ) : (
              <>
                {/* A NOTE, NOT A WALL. On Hjem the player is already named and
                the screen is theirs; what is missing is the club's half —
                bookings, activities, matches — and the note says so rather
                than letting empty sections read as «you have none». */}
                {tab === 'home' && (noClub || clubState) && (
                  <HomeClubNote
                    state={clubState ?? 'none'}
                    onChoose={openClubSheet}
                    onRetry={memberBootstrap?.retry}
                  />
                )}
                {tab === 'home' && (
                  <MobileHome
                    member={member}
                    myBookings={myBookings}
                    myActivities={myActivities}
                    next={next}
                    live={live}
                    nextMatch={nextMatch}
                    effectivePlayerDB={players}
                    courtMeta={courtMeta}
                    onGoTab={(t) => (t === 'play' ? goPlay('play') : setTab(t))}
                    onGoActivities={() => goPlay('activities')}
                    onGoLive={() => goMatches('live')}
                    onGoLeague={() => goMatches('league')}
                    onOpenElo={() => setSub('elo')}
                  />
                )}
                {tab === 'play' &&
                  (favoriteCenter ? (
                    <MobileCenterDetail
                      key={`play:${favoriteCenter.id}:${playTab}`}
                      center={favoriteCenter}
                      isFavorite={prefs.favoriteId === favoriteCenter.id}
                      asTab
                      initialTab={playTab}
                      onSwitchCenter={() => setSub('centers')}
                      onBack={() => setTab('home')}
                      {...centerDetailProps}
                    />
                  ) : (
                    <MobileCenters
                      centers={centers}
                      favoriteId={prefs.favoriteId}
                      filterId={centerFilter}
                      onFavorite={(id) => setPrefs({ favoriteId: id })}
                      onFilter={(id) => setPrefs({ filterId: id })}
                      onOpen={openCenter}
                      onBack={() => setTab('home')}
                    />
                  ))}
                {tab === 'reservations' && (
                  <MobileReservations
                    member={member}
                    bookings={bookings}
                    plannedMatches={plannedMatches}
                    activities={activities}
                    // EQUIPMENT (#911 slice 2): a machine booked on its own is a
                    // row of its own; one riding with a court is named on the
                    // court's row.
                    assets={assets}
                    assetBookings={assetBookings}
                    effectivePlayerDB={effectivePlayerDB}
                    courtMeta={courtMeta}
                    // «ALLE KLUBBER» (#760): an account has a person to ask
                    // about across clubs; a legacy member session and the
                    // portal preview do not.
                    acrossClubs={canSelectClub && proven}
                  />
                )}
                {tab === 'matches' && (
                  <MobileMatches
                    member={member}
                    bookings={bookings}
                    plannedMatches={plannedMatches}
                    singleMatches={singleMatches}
                    activities={activities}
                    effectivePlayerDB={effectivePlayerDB}
                    history={historyNoLeague}
                    live={live}
                    leagueLive={leagueLive}
                    leaguePast={leaguePast}
                    updatePlannedMatchPlayers={updatePlannedMatchPlayers}
                    completePlannedMatch={completePlannedMatch}
                    courtMeta={courtMeta}
                    calcPlayerPositionInTournament={calcPlayerPositionInTournament}
                    recordResultForTournament={recordResultForTournament}
                    recordKMResultForTournament={recordKMResultForTournament}
                    initialSegment={matchesSegment}
                    acrossClubs={canSelectClub && proven}
                  />
                )}
                {/* MESSAGING IS NOT AVAILABLE TO MEMBERS YET (#393).
                Meldinger lives in the club-scoped KV blob and a member session
                is deliberately not a club session, so there is nothing to read
                and nowhere to reply. Saying so is the honest option: an empty
                inbox reads as «no messages», and a send would vanish. Real
                member messaging is #236. */}
                {tab === 'messages' && messaging.unavailable && (
                  <div className="px-4 py-10 text-center">
                    <p className="font-semibold text-[15px]">
                      {tr('mobile.messages.unavailableTitle')}
                    </p>
                    <p className="mt-2 text-[13px] opacity-70">
                      {tr('mobile.messages.unavailableLead')}
                    </p>
                  </div>
                )}
                {tab === 'messages' && !messaging.unavailable && (
                  <MobileMessages
                    state={messaging.state}
                    member={member}
                    effectivePlayerDB={players}
                    clubInfo={clubInfo}
                    clubs={clubs}
                    onOpen={(id) => void openChat(id)}
                    onNew={() => setSheet('newMessage')}
                    onRejoinDynamic={(clubId) =>
                      void messaging.leaveDynamic(member?.id, clubId, false)
                    }
                    onSetDisabled={(d) => messaging.setDisabled(member?.id, d)}
                  />
                )}
                {tab === 'profile' && (
                  <MobileProfile
                    member={member}
                    singleMatches={singleMatches}
                    tournamentCount={history.length}
                    favoriteCenter={favoriteCenter?.name || null}
                    // NO DEMO SWITCH FOR A PROVEN IDENTITY (#791 review). The
                    // guarded `setMemberId` protects the WRITE; it does not stop a
                    // verified player being shown an identity-selection UI listing
                    // other players and having their choice silently ignored.
                    // Absent rather than disabled: the «Demo — real sign-in
                    // arrives once user accounts are in place» note under it is
                    // also no longer true for this person.
                    onSwitchClick={proven ? null : () => setSheet('member')}
                    onChooseClub={canSelectClub ? openClubSheet : null}
                    clubName={activeClub?.name ?? null}
                    onSignOut={proven ? onSignOut : null}
                    onOpenCenters={() => setSub('centers')}
                    push={push.supported ? push : undefined}
                    accountPasskeys={accountPasskeys}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <MobileTabBar
          className="ps-nav"
          value={tab}
          onChange={setTab}
          badges={{ messages: unread }}
        />
      </div>

      {sheet === 'newMessage' && member && (
        <RecipientPicker
          players={messagingPlayers(messaging.state, players)}
          allowClub={!isMessagingDisabled(messaging.state, CLUB_SENDER_ID)}
          meId={member.id}
          clubName={clubInfo?.name || tr('mobile.messaging.clubFallback')}
          onPickClub={async () => {
            const c = await messaging.openClubConversation(member.id);
            setSheet(null);
            void openChat(c.id);
          }}
          onPickMembers={async (ids, title) => {
            const c =
              ids.length === 1
                ? await messaging.openDm(member.id, ids[0])
                : await messaging.openGroup(member.id, ids, title);
            setSheet(null);
            void openChat(c.id);
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'club' && canSelectClub && (
        <ClubContextSheet
          {...clubContextProps}
          offerNone={activeClub != null}
          onOpenCentres={
            activeClub
              ? () => {
                  setSheet(null);
                  setSub('centers');
                }
              : null
          }
          onClose={() => setSheet(null)}
        />
      )}
      {/* BOTH GATES, deliberately (#791 review): the CTA above is how the
          sheet is opened today, and this is what makes it unreachable by any
          other path. `mustPick` can no longer be true for a proven identity —
          `!proven` here says the manual sheet cannot be either. */}
      {!proven && (sheet === 'member' || mustPick) && (
        <MemberPicker
          players={players}
          currentId={member?.id}
          dismissible={!mustPick}
          title={mustPick ? tr('mobile.memberPicker.title') : tr('mobile.memberPicker.switch')}
          hint={
            mustPick ? tr('mobile.memberPicker.hintFirst') : tr('mobile.memberPicker.hintSwitch')
          }
          onPick={async (id) => {
            await setMemberId(id);
            setSheet(null);
          }}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}

/** The one-line note above Hjem while the club's half is missing. */
function HomeClubNote({
  state,
  onChoose,
  onRetry,
}: {
  state: 'none' | 'loading' | 'failed';
  onChoose: () => void;
  onRetry?: () => void;
}) {
  const t = useT();
  const text =
    state === 'none'
      ? t('mobile.clubContext.homeNone')
      : state === 'loading'
        ? t('mobile.club.loading')
        : t('mobile.club.failedTitle');
  return (
    <div className="px-4 pt-2">
      <div
        className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2"
        style={{ background: '#fff', border: '1px dashed #e7eae3' }}
        role="status"
      >
        <span className="text-[12px] text-ink-2 min-w-0">
          {text}
          {/* The reassurance travels with the failure, as on the full panel:
              the data is not gone, the answer is. */}
          {state === 'failed' && (
            <span className="block text-ink-3">{t('mobile.club.failedBody')}</span>
          )}
        </span>
        {state === 'none' && (
          <button
            type="button"
            onClick={onChoose}
            className="text-[12px] font-semibold text-ink underline flex-shrink-0"
          >
            {t('mobile.clubContext.choose')}
          </button>
        )}
        {state === 'failed' && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="text-[12px] font-semibold text-ink underline flex-shrink-0"
          >
            {t('mobile.identity.retry')}
          </button>
        )}
      </div>
    </div>
  );
}
